import React from 'react';
import { Check, ArrowRight, Play, AlertTriangle } from 'lucide-react';
import { usePaywallCheck } from '@/hooks/usePaywallCheck';
import { usePaywallStore } from '@/stores/paywallStore';

/**
 * Horizontal banner on the Dashboard when user needs to upgrade or start a trial.
 * Non-blocking — sits between hero section and recent canvases.
 * Click CTA → opens PaywallModal or ExhaustedTrialModal.
 *
 * Handles all 5 scenarios per the Touchpoint Matrix.
 */
export function DashboardPaywallBanner() {
  const { needsUpgrade, needsTrial, isCancelledTrial, isExhaustedTrial, isExhaustedPaid, currentPeriodEnd } = usePaywallCheck();
  const openPaywall = usePaywallStore((s) => s.openPaywall);

  // Determine which scenario we're in (priority order matters)
  const isAnyPaywalled = needsTrial || isCancelledTrial || isExhaustedTrial || isExhaustedPaid || needsUpgrade;
  if (!isAnyPaywalled) return null;

  let formattedDate = 'soon';
  if (currentPeriodEnd) {
    const endDate = new Date(currentPeriodEnd);
    const diffDays = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    const dateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    formattedDate = diffDays <= 0 ? 'today' : diffDays === 1 ? 'tomorrow' : diffDays <= 14 ? `in ${diffDays} days` : dateStr;
  }

  // Scenario config
  let headline: string;
  let details: React.ReactNode;
  let ctaText: string;
  let ctaIcon: React.ReactNode;
  let variant: Parameters<typeof openPaywall>[1];

  if (needsTrial) {
    // Scenario A: Fresh alpha user
    headline = 'Ready to Create? Start Your Free Trial';
    details = (
      <>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />30+ image models</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />20+ video models</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />7-day free trial</span>
      </>
    );
    ctaText = 'Start Free Trial';
    ctaIcon = <Play className="w-4 h-4" />;
    variant = 'trial';
  } else if (isCancelledTrial) {
    // Scenario B: Cancelled trial
    headline = 'Your Trial Was Cancelled';
    details = (
      <>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />30+ image models</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />20+ video models</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />From $19/mo</span>
      </>
    );
    ctaText = 'View Plans';
    ctaIcon = <ArrowRight className="w-4 h-4" />;
    variant = 'cancelled_trial';
  } else if (isExhaustedTrial) {
    // Scenario C: Exhausted trial
    headline = "You've Used Your Trial Credits";
    details = (
      <>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />Plan starts {formattedDate}</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />4,000 credits on activation</span>
      </>
    );
    ctaText = 'Start Plan Now';
    ctaIcon = <ArrowRight className="w-4 h-4" />;
    variant = 'exhausted_trial';
  } else if (isExhaustedPaid) {
    // Scenario D: Paid active, out of credits
    headline = "You've Run Out of Credits";
    details = (
      <>
        <span className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" style={{ color: '#fb923c' }} />Credits refresh {formattedDate}</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />Or upgrade your plan</span>
      </>
    );
    ctaText = 'View Plans';
    ctaIcon = <ArrowRight className="w-4 h-4" />;
    variant = 'exhausted';
  } else {
    // Scenario E: Expired/cancelled subscription
    headline = 'Your Subscription Has Ended';
    details = (
      <>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />30+ image models</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />20+ video models</span>
        <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-accent-400" />From $19/mo</span>
      </>
    );
    ctaText = 'View Plans';
    ctaIcon = <ArrowRight className="w-4 h-4" />;
    variant = 'upgrade';
  }

  // Warning theme for exhausted trial (Scenario C) — same navy base with subtle amber accents
  const isWarningTheme = isExhaustedTrial;
  const bgGradient = 'linear-gradient(135deg, #1d2939 0%, #2d3546 40%, #34526b 70%, #3d6483 100%)';
  const borderColor = isWarningTheme
    ? '1px solid rgba(251,191,36,0.12)'
    : '1px solid rgba(255,255,255,0.08)';
  const orbGradient = isWarningTheme
    ? 'radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 70%)'
    : 'radial-gradient(circle, rgba(200,212,90,0.4) 0%, transparent 70%)';

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-10"
      style={{
        background: bgGradient,
        border: borderColor,
      }}
    >
      {/* Decorative gradient orb */}
      <div
        className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20 pointer-events-none"
        style={{
          background: orbGradient,
        }}
      />

      <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8">
        {/* Left: Message */}
        <div className="flex-1 min-w-0">
          <h3 className="text-xl sm:text-2xl font-serif font-bold text-white mb-2">
            {headline}
          </h3>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-white/50">
            {details}
          </div>
        </div>

        {/* Right: CTA */}
        <button
          onClick={() => openPaywall('dashboard', variant)}
          className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #c8d45a 0%, #a8b44a 100%)',
            color: '#1d2939',
            boxShadow: '0 4px 20px rgba(200,212,90,0.25), 0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {ctaText}
          {ctaIcon}
        </button>
      </div>
    </div>
  );
}
